import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Lock, LogOut, MapPin, MessageCircleQuestion, Moon, PenLine, ReceiptText } from 'lucide-react';
import {
  ActionList,
  Avatar,
  Button,
  LegalFooter,
  PageHeader,
  SectionHeader,
  SegmentedTabs,
  ThemePreference,
  useRequestLogout,
  useTheme,
} from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import { formatIndianMobile } from '@/lib/support';
import { formatAddress } from '@/utils/address';
import { GST_PATH } from '@/utils/gst';
import { NOTIFICATION_SETTINGS_PATH } from '@/utils/notifications';
import { comingSoon } from '@/utils/orderPresentation';

const APPEARANCE: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** T5 Account: profile, account settings, support & legal, and log out. */
const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preference, setPreference } = useTheme();
  const requestLogout = useRequestLogout();

  const phone = formatIndianMobile(user?.phone);
  const contact = phone ? `${phone} · Verified by OTP` : user?.email;
  const address = formatAddress(user?.address);

  return (
    <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-6">
      <PageHeader title="Account" subtitle="Your profile, preferences and invoices." />

      <section aria-label="Profile" className="tw-flex tw-items-center tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
        <Avatar name={user?.name} size="lg" />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
          <p className="tw-text-[16px] tw-font-semibold tw-leading-[22px] tw-text-ds-text-primary">{user?.name || 'Your account'}</p>
          {contact && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{contact}</p>}
        </div>
        <Button size="sm" variant="secondary" leadingIcon={PenLine} onClick={() => comingSoon('Editing your profile')}>
          Edit
        </Button>
      </section>

      <section className="tw-flex tw-flex-col tw-gap-4">
        <SectionHeader title="Account" />
        <ActionList
          items={[
            {
              icon: MapPin,
              title: 'Saved addresses',
              description: address ?? 'No saved address yet',
              onSelect: () => comingSoon('Managing addresses'),
            },
            { icon: ReceiptText, title: 'GST details', description: 'GSTIN and tax invoices for your plans', onSelect: () => navigate(GST_PATH) },
            { icon: Bell, title: 'Notification settings', description: 'Choose how we update you', onSelect: () => navigate(NOTIFICATION_SETTINGS_PATH) },
            {
              icon: Moon,
              title: 'Appearance',
              description: preference === 'system' ? 'System default' : preference === 'dark' ? 'Dark' : 'Light',
              trailing: <SegmentedTabs items={APPEARANCE} value={preference} onChange={setPreference} label="Appearance" className="tw-shrink-0" />,
            },
          ]}
        />
      </section>

      <section className="tw-flex tw-flex-col tw-gap-4">
        <SectionHeader title="Support & legal" />
        <ActionList
          items={[
            { icon: MessageCircleQuestion, title: 'Help & Support', description: 'FAQs, WhatsApp and email', onSelect: () => navigate('/customer/support') },
            { icon: FileText, title: 'Terms of use', description: 'How Protega plans work', onSelect: () => navigate('/terms') },
            { icon: Lock, title: 'Privacy policy', description: 'How we use your data', onSelect: () => navigate('/privacy-policy') },
          ]}
        />
      </section>

      <Button variant="danger" fullWidth leadingIcon={LogOut} onClick={requestLogout}>
        Log out
      </Button>

      <LegalFooter layout="centered" />
    </div>
  );
};

export default AccountPage;
